// /pillbox — visual representation of how the user should fill a
// weekly pill organizer for each time of day. Senior-friendly: large
// colored circles with the medication's initial, full name below.

import type { Medication } from '@medreview/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMedications } from '../api/medications';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { ApiError } from '../components/senior/ApiError';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { useAdherence } from '../hooks/useAdherence';
import type { AdherenceSlot } from '../hooks/useAdherence';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useCelebrate } from '../hooks/useCelebrate';
import { useMe } from '../hooks/useMe';
import { useSoundEffect } from '../hooks/useSoundEffect';
import { AppPage } from '../layouts/AppPage';
import { drugClassColor } from '../lib/drugClassColor';

// Streak day-counts where we fire celebration confetti. Tuned for
// senior delight — every milestone matters but not every day.
const STREAK_MILESTONES = new Set([3, 7, 14, 30, 60, 90, 180, 365]);
import { PILL_SLOTS, TIME_SLOT_LABEL, bucketize } from '../lib/pillSchedule';

const SLOT_ICON: Record<string, string> = {
  morning: '🌅',
  noon: '☀️',
  evening: '🌆',
  bedtime: '🌙',
};

export function Pillbox(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [meds, setMeds] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listMedications(authedFetch);
        if (!cancelled) setMeds(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  const buckets = bucketize(meds);
  const adherence = useAdherence();
  const { me } = useMe();
  const celebrate = useCelebrate();
  const playSound = useSoundEffect();
  const lastCelebratedStreak = useRef<number>(0);

  // Fire confetti when the user crosses a milestone streak day. Guarded
  // so it only fires once per unique milestone day (won't re-fire on
  // every render at the same streak).
  useEffect(() => {
    if (
      STREAK_MILESTONES.has(adherence.streak) &&
      adherence.streak !== lastCelebratedStreak.current
    ) {
      lastCelebratedStreak.current = adherence.streak;
      celebrate('streak', `You hit a ${adherence.streak}-day streak. Beautiful, keep going.`);
    }
  }, [adherence.streak, celebrate]);

  return (
    <AppPage title="Daily pillbox" back={{ to: '/medications', label: 'Medicines' }}>
      {adherence.streak > 0 ? (
        <section
          className="account-section"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}
        >
          <span
            aria-hidden="true"
            style={{
              fontSize: 36,
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'var(--gradient-warm)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            🔥
          </span>
          <div>
            <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
              {adherence.streak} day streak
            </p>
            <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '2px 0 0' }}>
              You have taken at least one medicine every day for {adherence.streak}{' '}
              {adherence.streak === 1 ? 'day' : 'days'} in a row.
            </p>
          </div>
        </section>
      ) : null}
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Siggy says</p>
          <p className="food-intro__text">
            Here is what each compartment of your pillbox should look like. Print this page or keep
            it open while you fill a weekly organizer.
          </p>
        </div>
      </section>

      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}
      {error ? (
        <ApiError
          title="Could not load your pillbox"
          error={error}
          onRetry={() => window.location.reload()}
        />
      ) : null}

      {!loading && meds.length === 0 ? (
        <EmptyIllustration
          kind="pillbox"
          title="No medicines yet"
          body="Add medicines and we will arrange them by time of day automatically."
          cta={
            <Link to="/medications/add-by-photo" className="home-hero__cta">
              Add a medicine
            </Link>
          }
        />
      ) : null}

      {!loading && meds.length > 0 ? (
        <div className="pillbox-grid">
          {PILL_SLOTS.map((slot) => (
            <section key={slot} className="pillbox-compartment">
              <h3 className="pillbox-compartment__title">
                <span aria-hidden="true">{SLOT_ICON[slot]}</span>
                <span>{TIME_SLOT_LABEL[slot]}</span>
                <span className="pillbox-count">{buckets[slot].length}</span>
              </h3>
              {buckets[slot].length === 0 ? (
                <p className="pillbox-empty">No medicines at this time.</p>
              ) : (
                <ul className="pillbox-list">
                  {buckets[slot].map((b) => {
                    const palette = drugClassColor(b.med.drugClasses, b.med.name);
                    const taken = adherence.isTaken(b.med.id, slot as AdherenceSlot);
                    return (
                      <li
                        key={b.med.id}
                        className="pillbox-pill"
                        style={{ gridTemplateColumns: 'auto 1fr auto' }}
                      >
                        <span
                          className="pillbox-circle"
                          style={{
                            background: palette.bg,
                            color: palette.fg,
                            opacity: taken ? 0.5 : 1,
                          }}
                          aria-hidden="true"
                        >
                          {b.initial}
                        </span>
                        <div>
                          <p
                            className="pillbox-name"
                            style={{
                              textDecoration: taken ? 'line-through' : 'none',
                              color: taken ? 'var(--color-text-secondary)' : undefined,
                            }}
                          >
                            {b.med.name}
                          </p>
                          <p className="pillbox-sub">
                            {[b.med.strength, b.med.frequency].filter(Boolean).join(' • ')}
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`adh-check${taken ? ' is-taken' : ''}`}
                          onClick={() => {
                            // Play the soft ding only on the affirmative
                            // direction (not-taken -> taken) so undoing
                            // doesn't audibly reward an undo. Default OFF
                            // when the user has flagged hearing concerns —
                            // a tinnitus user (round-4 Sean) or hearing-aid
                            // wearer (round-4 Ba-Thu's mom) experiences
                            // this as hostile. Settings will add an
                            // explicit "Sound effects" toggle in Tier 4.
                            if (!taken && !me?.accessibility?.hearingConcern) {
                              playSound('med-check-ding');
                            }
                            void adherence.toggle(b.med.id, slot as AdherenceSlot);
                          }}
                          aria-pressed={taken}
                          aria-label={taken ? 'Mark as not taken' : 'Mark as taken'}
                          title={taken ? 'Tap to undo' : 'Mark taken'}
                        >
                          {taken ? (
                            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                              <title>Taken</title>
                              <path
                                d="M5 12l4 4 10-10"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <span aria-hidden="true" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))}
        </div>
      ) : null}

      {!loading && meds.length > 0 ? (
        <section className="account-section">
          <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: 0 }}>
            We grouped your medicines by what we could read from each frequency. If a medicine shows
            up in every compartment, double-check the label. We could not tell when you should take
            it. Always follow the directions on the bottle.
          </p>
        </section>
      ) : null}
    </AppPage>
  );
}
